import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Plus, Edit, Trash2, Search, Camera, ArrowRightLeft,
  MapPin, User, Phone, Mail, Building2, ClipboardCheck,
  ChevronDown, ChevronUp, X, Image as ImageIcon, Eye, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

const MEMORIAL_TYPES = [
  'Headstone', 'Lawn Memorial', 'Kerb Set', 'Vase', 'Plaque',
  'Tablet', 'Cremation Memorial', 'Full Memorial', 'Other'
];

const STONE_TYPES = ['Granite', 'Marble', 'Slate', 'Sandstone', 'Limestone', 'Other'];

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  collected: { label: 'Collected', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  in_storage: { label: 'In Storage', color: 'bg-green-100 text-green-800 border-green-300' },
  converted_to_order: { label: 'Converted', color: 'bg-purple-100 text-purple-800 border-purple-300' }
};

function Removals() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [removals, setRemovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusCounts, setStatusCounts] = useState({});

  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  // Resend Collection Form dialog
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendRemoval, setResendRemoval] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [sendingResend, setSendingResend] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCollectionDialog, setShowCollectionDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedRemoval, setSelectedRemoval] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const [formData, setFormData] = useState({
    name_on_stone: '', requestor_type: 'client', requestor_name: '',
    requestor_phone: '', requestor_email: '', requestor_company: '',
    cemetery_name: '', cemetery_location: '', plot_reference: '',
    memorial_type: '', stone_type: '', stone_colour: '', description: '',
    funeral_date: '', scheduled_collection_date: ''
  });

  const [collectionForm, setCollectionForm] = useState({
    collected_by: '', collection_location: '', collection_notes: ''
  });
  const [collectionPhoto, setCollectionPhoto] = useState(null);
  const [convertOrderType, setConvertOrderType] = useState('add on inscription');
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchRemovals(); }, [statusFilter, sortBy, sortOrder]);

  const fetchRemovals = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const response = await axios.get(`${API}/api/removals?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRemovals(response.data.removals || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      toast.error('Failed to load removals');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name_on_stone: '', requestor_type: 'client', requestor_name: '',
      requestor_phone: '', requestor_email: '', requestor_company: '',
      cemetery_name: '', cemetery_location: '', plot_reference: '',
      memorial_type: '', stone_type: '', stone_colour: '', description: '',
      funeral_date: '', scheduled_collection_date: ''
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRemovals();
  };

  const handleCreate = async () => {
    if (!formData.name_on_stone || !formData.requestor_name || !formData.cemetery_name) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/api/removals`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removal request created');
      setShowAddDialog(false);
      resetForm();
      fetchRemovals();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create removal'); toast.error(msg);
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/api/removals/${selectedRemoval.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removal updated');
      setShowEditDialog(false);
      setSelectedRemoval(null);
      resetForm();
      fetchRemovals();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update removal'); toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this removal request?')) return;
    try {
      await axios.delete(`${API}/api/removals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removal deleted');
      fetchRemovals();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete removal'); toast.error(msg);
    }
  };

  const handleUploadImages = async (removalId, files) => {
    setUploadingImages(true);
    try {
      const formDataUpload = new FormData();
      for (const file of files) {
        formDataUpload.append('files', file);
      }
      await axios.post(`${API}/api/removals/${removalId}/images`, formDataUpload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Images uploaded');
      fetchRemovals();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to upload images'); toast.error(msg);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (removalId, index) => {
    try {
      await axios.delete(`${API}/api/removals/${removalId}/images/${index}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image removed');
      fetchRemovals();
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const handleCollection = async () => {
    if (!collectionForm.collected_by || !collectionForm.collection_location) {
      toast.error('Please fill in required collection fields');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('collected_by', collectionForm.collected_by);
      fd.append('collection_location', collectionForm.collection_location);
      if (collectionForm.collection_notes) fd.append('collection_notes', collectionForm.collection_notes);
      if (collectionPhoto) fd.append('photo', collectionPhoto);

      await axios.put(`${API}/api/removals/${selectedRemoval.id}/collection`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Collection details saved - memorial marked as collected');
      setShowCollectionDialog(false);
      setSelectedRemoval(null);
      setCollectionForm({ collected_by: '', collection_location: '', collection_notes: '' });
      setCollectionPhoto(null);
      fetchRemovals();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save collection details'); toast.error(msg);
    }
  };

  const handleConvert = async () => {
    try {
      const response = await axios.post(
        `${API}/api/removals/${selectedRemoval.id}/convert-to-order`,
        { order_type: convertOrderType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Converted to order #${response.data.order_number}`);
      setShowConvertDialog(false);
      setSelectedRemoval(null);
      fetchRemovals();
      // Navigate to the new order
      navigate(`/orders/${response.data.order_id}`);
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to convert to order'); toast.error(msg);
    }
  };

  const handleStatusChange = async (removalId, newStatus) => {
    try {
      await axios.put(`${API}/api/removals/${removalId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchRemovals();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleResendCollectionForm = async () => {
    // Send the collection-confirmation form to the entered email. Defaults to
    // the requestor's address but the input is editable so staff can punch in
    // any alternate / additional recipient.
    if (!resendRemoval) return;
    const target = (resendEmail || '').trim();
    if (!target || !target.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSendingResend(true);
    try {
      const r = await axios.post(
        `${API}/api/removals/${resendRemoval.id}/resend-collection-form`,
        { to_email: target },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Collection form sent to ${r.data?.sent_to || target}`);
      setShowResendDialog(false);
      setResendRemoval(null);
      setResendEmail('');
      fetchRemovals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send collection form');
    } finally {
      setSendingResend(false);
    }
  };

  const openEditDialog = (removal) => {
    setSelectedRemoval(removal);
    setFormData({
      name_on_stone: removal.name_on_stone || '',
      requestor_type: removal.requestor_type || 'client',
      requestor_name: removal.requestor_name || '',
      requestor_phone: removal.requestor_phone || '',
      requestor_email: removal.requestor_email || '',
      requestor_company: removal.requestor_company || '',
      cemetery_name: removal.cemetery_name || '',
      cemetery_location: removal.cemetery_location || '',
      plot_reference: removal.plot_reference || '',
      memorial_type: removal.memorial_type || '',
      stone_type: removal.stone_type || '',
      stone_colour: removal.stone_colour || '',
      description: removal.description || '',
      funeral_date: removal.funeral_date ? removal.funeral_date.split('T')[0] : '',
      scheduled_collection_date: removal.scheduled_collection_date ? removal.scheduled_collection_date.split('T')[0] : ''
    });
    setShowEditDialog(true);
  };

  const totalRemovals = removals.length;
  const pendingCount = statusCounts.pending || 0;
  const collectedCount = statusCounts.collected || 0;
  const inStorageCount = statusCounts.in_storage || 0;
  const convertedCount = statusCounts.converted_to_order || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-slate-600">Loading removals...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-slate-800">{pendingCount + collectedCount + inStorageCount + convertedCount}</div>
          <div className="text-sm text-slate-600">Total Removals</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
          <div className="text-sm text-slate-600">Pending Collection</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-blue-600">{collectedCount}</div>
          <div className="text-sm text-slate-600">Collected</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-2xl font-bold text-green-600">{inStorageCount}</div>
          <div className="text-sm text-slate-600">In Storage</div>
        </CardContent></Card>
      </div>

      {/* Search & Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name on stone, customer, cemetery, ref..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchRemovals()}
                  className="pl-10"
                  data-testid="removals-search"
                />
              </div>
            </form>
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'collected', 'in_storage', 'converted_to_order'].map(s => (
                <Button
                  key={s}
                  variant={statusFilter === s ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(s)}
                  size="sm"
                  className={statusFilter === s && s !== 'all' ? STATUS_CONFIG[s]?.color : ''}
                  data-testid={`filter-${s}`}
                >
                  {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex gap-4 mt-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span>Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value="created_at">Date Added</option>
                <option value="name_on_stone">Name on Stone</option>
                <option value="requestor_name">Customer Name</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="p-1"
              >
                {sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Removals List */}
      <Card>
        <CardContent className="p-0">
          {removals.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No removal requests found</p>
              <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowAddDialog(true); }}>
                Add your first removal
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {removals.map((item) => (
                <RemovalRow
                  key={item.id}
                  item={item}
                  expanded={expandedRow === item.id}
                  onToggle={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
                  onEdit={() => openEditDialog(item)}
                  onDelete={() => handleDelete(item.id)}
                  onCollect={() => { setSelectedRemoval(item); setShowCollectionDialog(true); }}
                  onConvert={() => { setSelectedRemoval(item); setConvertOrderType('add on inscription'); setShowConvertDialog(true); }}
                  onResendForm={() => { setResendRemoval(item); setResendEmail(item.requestor_email || ''); setShowResendDialog(true); }}
                  onUploadImages={(files) => handleUploadImages(item.id, files)}
                  onDeleteImage={(idx) => handleDeleteImage(item.id, idx)}
                  onStatusChange={(status) => handleStatusChange(item.id, status)}
                  uploadingImages={uploadingImages}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Button - floating */}
      <div className="mt-6">
        <Button
          onClick={() => { resetForm(); setShowAddDialog(true); }}
          className="bg-orange-600 hover:bg-orange-700"
          data-testid="add-removal-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Removal Request
        </Button>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Removal Request</DialogTitle>
          </DialogHeader>
          <RemovalForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="save-removal-btn">
              Create Removal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Removal Request</DialogTitle>
          </DialogHeader>
          <RemovalForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-orange-600 hover:bg-orange-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collection Form Dialog */}
      <Dialog open={showCollectionDialog} onOpenChange={setShowCollectionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
              Collection Form
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800">Collecting: {selectedRemoval?.name_on_stone}</p>
              <p className="text-blue-600">{selectedRemoval?.cemetery_name} {selectedRemoval?.plot_reference ? `- Plot ${selectedRemoval.plot_reference}` : ''}</p>
            </div>
            <div>
              <Label>Collected By *</Label>
              <Input
                value={collectionForm.collected_by}
                onChange={(e) => setCollectionForm({...collectionForm, collected_by: e.target.value})}
                placeholder="Staff member name"
                data-testid="collection-collected-by"
              />
            </div>
            <div>
              <Label>Collection Location *</Label>
              <Input
                value={collectionForm.collection_location}
                onChange={(e) => setCollectionForm({...collectionForm, collection_location: e.target.value})}
                placeholder="Where is the memorial being taken to?"
                data-testid="collection-location"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={collectionForm.collection_notes}
                onChange={(e) => setCollectionForm({...collectionForm, collection_notes: e.target.value})}
                placeholder="Condition of memorial, any issues..."
                rows={3}
              />
            </div>
            <div>
              <Label>Collection Photo</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setCollectionPhoto(e.target.files[0])}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCollectionDialog(false)}>Cancel</Button>
            <Button onClick={handleCollection} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-collection-btn">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Mark as Collected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Order Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-600" />
              Convert to Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-purple-800">{selectedRemoval?.name_on_stone}</p>
              <p className="text-purple-600">Ref: {selectedRemoval?.removal_ref}</p>
              <p className="text-purple-600">Requestor: {selectedRemoval?.requestor_name}</p>
            </div>
            <div>
              <Label>Order Type *</Label>
              <Select value={convertOrderType} onValueChange={setConvertOrderType}>
                <SelectTrigger data-testid="convert-order-type">
                  <SelectValue placeholder="Select order type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add on inscription">Add On Inscription</SelectItem>
                  <SelectItem value="maintenance">Maintenance / Clean Up</SelectItem>
                  <SelectItem value="restoration">Restoration</SelectItem>
                  <SelectItem value="new memorial">New Memorial (Replacement)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-slate-500">
              The removal details will be transferred to the new order. You can add more details (pricing, inscription, etc.) on the order page.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
            <Button onClick={handleConvert} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-convert-btn">
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Convert to Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Collection Form Dialog */}
      <Dialog open={showResendDialog} onOpenChange={setShowResendDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Resend Collection Form
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-900">{resendRemoval?.name_on_stone}</p>
              <p className="text-blue-700">Ref: {resendRemoval?.removal_ref}</p>
              <p className="text-blue-700">Cemetery: {resendRemoval?.cemetery_name}</p>
            </div>
            <div>
              <Label>Recipient email *</Label>
              <Input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Enter email to send the collection form to"
                data-testid="resend-collection-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Defaults to the requestor email on the record. You can change it to send to an alternate address (cemetery office, family, colleague…).
              </p>
            </div>
            {resendRemoval?.collection_form_sends?.length > 0 && (
              <div className="text-xs text-slate-500 space-y-1">
                <p className="font-medium text-slate-600">Previously sent to:</p>
                {resendRemoval.collection_form_sends.slice(-3).reverse().map((s, i) => (
                  <p key={i}>• {s.sent_to} — {new Date(s.sent_at).toLocaleString()}</p>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResendDialog(false)}>Cancel</Button>
            <Button
              onClick={handleResendCollectionForm}
              disabled={sendingResend}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-resend-collection-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingResend ? 'Sending…' : 'Send Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Removal Row Component
function RemovalRow({ item, expanded, onToggle, onEdit, onDelete, onCollect, onConvert, onResendForm, onUploadImages, onDeleteImage, onStatusChange, uploadingImages }) {
  const fileInputRef = useRef(null);
  const statusConf = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
  const createdDate = item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB') : '-';

  return (
    <div className="hover:bg-slate-50/50" data-testid={`removal-row-${item.id}`}>
      <div className="flex items-center p-4 cursor-pointer" onClick={onToggle}>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-2 md:gap-4 items-center">
          {/* Ref + Name on Stone */}
          <div>
            <span className="font-mono text-xs font-bold bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
              {item.removal_ref || 'N/A'}
            </span>
            <div className="font-medium text-slate-800 mt-1">{item.name_on_stone}</div>
          </div>
          {/* Requestor */}
          <div>
            <div className="text-sm text-slate-800 flex items-center gap-1">
              <User className="w-3 h-3 text-slate-400" />
              {item.requestor_name}
            </div>
            {item.requestor_company && (
              <div className="text-xs text-slate-500 flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                {item.requestor_company}
              </div>
            )}
            <Badge variant="outline" className="text-xs mt-1">
              {item.requestor_type === 'funeral_director' ? 'Funeral Director' : 'Client'}
            </Badge>
          </div>
          {/* Cemetery */}
          <div>
            <div className="text-sm text-slate-700 flex items-center gap-1">
              <MapPin className="w-3 h-3 text-slate-400" />
              {item.cemetery_name}
            </div>
            {item.plot_reference && <div className="text-xs text-slate-500">Plot: {item.plot_reference}</div>}
          </div>
          {/* Date + Status */}
          <div>
            <div className="text-xs text-slate-500">{createdDate}</div>
            <Badge className={`text-xs mt-1 ${statusConf.color}`}>{statusConf.label}</Badge>
            {item.scheduled_collection_date && (
              <div className="text-xs text-blue-600 mt-1">
                Collect: {new Date(item.scheduled_collection_date).toLocaleDateString('en-GB')}
              </div>
            )}
            {item.funeral_date && (
              <div className="text-xs text-purple-600">
                Funeral: {new Date(item.funeral_date).toLocaleDateString('en-GB')}
              </div>
            )}
          </div>
          {/* Images count */}
          <div className="flex items-center gap-2">
            {item.images?.length > 0 && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {item.images.length}/3
              </span>
            )}
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </div>

      {/* Expanded Detail */}
      {expanded && (
        <div className="px-4 pb-4 border-t bg-slate-50/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
            {/* Details Column */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-slate-700">Memorial Details</h4>
              {item.memorial_type && <div className="text-sm"><span className="text-slate-500">Type:</span> {item.memorial_type}</div>}
              {item.stone_type && <div className="text-sm"><span className="text-slate-500">Stone:</span> {item.stone_type}</div>}
              {item.stone_colour && <div className="text-sm"><span className="text-slate-500">Colour:</span> {item.stone_colour}</div>}
              {item.cemetery_location && <div className="text-sm"><span className="text-slate-500">Location:</span> {item.cemetery_location}</div>}
              {item.description && <div className="text-sm text-slate-600 mt-2 bg-white p-2 rounded border">{item.description}</div>}
              {/* Dates */}
              {(item.funeral_date || item.scheduled_collection_date) && (
                <div className="mt-3 p-2 bg-purple-50 border border-purple-200 rounded space-y-1">
                  <h5 className="font-medium text-xs text-purple-800">Key Dates</h5>
                  {item.funeral_date && (
                    <div className="text-sm text-purple-700">Funeral: {new Date(item.funeral_date).toLocaleDateString('en-GB')}</div>
                  )}
                  {item.scheduled_collection_date && (
                    <div className="text-sm text-purple-700">Scheduled Collection: {new Date(item.scheduled_collection_date).toLocaleDateString('en-GB')}</div>
                  )}
                  {item.calendar_task_id && (
                    <div className="text-xs text-purple-500 mt-1">Linked to calendar</div>
                  )}
                </div>
              )}
            </div>

            {/* Contact Column */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-slate-700">Contact Details</h4>
              <div className="text-sm flex items-center gap-1"><User className="w-3 h-3 text-slate-400" />{item.requestor_name}</div>
              {item.requestor_phone && <div className="text-sm flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{item.requestor_phone}</div>}
              {item.requestor_email && <div className="text-sm flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{item.requestor_email}</div>}
              {item.requestor_company && <div className="text-sm flex items-center gap-1"><Building2 className="w-3 h-3 text-slate-400" />{item.requestor_company}</div>}

              {/* Collection Details */}
              {item.collected_by && (
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                  <h5 className="font-medium text-xs text-blue-800">Collection Info</h5>
                  <div className="text-xs text-blue-700 mt-1">By: {item.collected_by}</div>
                  {item.collection_location && <div className="text-xs text-blue-700">Location: {item.collection_location}</div>}
                  {item.collection_notes && <div className="text-xs text-blue-700">Notes: {item.collection_notes}</div>}
                  {item.collection_date && <div className="text-xs text-blue-700">Date: {new Date(item.collection_date).toLocaleDateString('en-GB')}</div>}
                </div>
              )}

              {/* Converted Order Link */}
              {item.converted_order_id && (
                <div className="mt-3 p-2 bg-purple-50 border border-purple-200 rounded">
                  <a href={`/orders/${item.converted_order_id}`} className="text-sm text-purple-700 font-medium hover:underline">
                    Order #{item.converted_order_number} ({item.converted_order_type})
                  </a>
                </div>
              )}
            </div>

            {/* Images Column */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-slate-700">Photos ({item.images?.length || 0}/3)</h4>
              <div className="grid grid-cols-3 gap-2">
                {(item.images || []).map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={`${API_BASE}${img}`}
                      alt={`Memorial ${idx + 1}`}
                      className="w-full h-20 object-cover rounded border"
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteImage(idx); }}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {(item.images?.length || 0) < 3 && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const maxAllowed = 3 - (item.images?.length || 0);
                      if (files.length > maxAllowed) {
                        toast.error(`Only ${maxAllowed} more image(s) allowed`);
                        return;
                      }
                      onUploadImages(files);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    disabled={uploadingImages}
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    {uploadingImages ? 'Uploading...' : 'Add Photo'}
                  </Button>
                </>
              )}
              {item.collection_photo_url && (
                <div className="mt-2">
                  <div className="text-xs text-slate-500 mb-1">Collection Photo:</div>
                  <img src={`${API_BASE}${item.collection_photo_url}`} alt="Collection" className="w-full h-20 object-cover rounded border" />
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t">
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
            {item.status === 'pending' && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={(e) => { e.stopPropagation(); onCollect(); }} data-testid={`collect-btn-${item.id}`}>
                <ClipboardCheck className="w-4 h-4 mr-1" /> Fill Collection Form
              </Button>
            )}
            {item.status === 'collected' && (
              <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={(e) => { e.stopPropagation(); onStatusChange('in_storage'); }}>
                Move to Storage
              </Button>
            )}
            {/* Resend Collection Form — only meaningful once the memorial has
                actually been collected; allows the staff to send the original
                confirmation form to a different recipient (alt email). */}
            {['collected', 'in_storage'].includes(item.status) && (
              <Button
                size="sm"
                variant="outline"
                className="text-blue-700 border-blue-300 hover:bg-blue-50"
                onClick={(e) => { e.stopPropagation(); onResendForm(); }}
                data-testid={`resend-collection-form-${item.id}`}
              >
                <Send className="w-4 h-4 mr-1" /> Resend Form
              </Button>
            )}
            {item.status !== 'converted_to_order' && (
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={(e) => { e.stopPropagation(); onConvert(); }} data-testid={`convert-btn-${item.id}`}>
                <ArrowRightLeft className="w-4 h-4 mr-1" /> Convert to Order
              </Button>
            )}
            {item.status !== 'converted_to_order' && (
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Removal Form Component
function RemovalForm({ formData, setFormData }) {
  return (
    <div className="space-y-4 py-4">
      {/* Stone/Memorial Info */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h3 className="font-medium text-orange-800 mb-3">Memorial Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Name on Stone *</Label>
            <Input
              value={formData.name_on_stone}
              onChange={(e) => setFormData({...formData, name_on_stone: e.target.value})}
              placeholder="Name(s) on the memorial"
              data-testid="removal-name-on-stone"
            />
          </div>
          <div>
            <Label>Memorial Type</Label>
            <Select value={formData.memorial_type} onValueChange={(v) => setFormData({...formData, memorial_type: v})}>
              <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {MEMORIAL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stone Type</Label>
            <Select value={formData.stone_type} onValueChange={(v) => setFormData({...formData, stone_type: v})}>
              <SelectTrigger><SelectValue placeholder="Select stone" /></SelectTrigger>
              <SelectContent>
                {STONE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stone Colour</Label>
            <Input
              value={formData.stone_colour}
              onChange={(e) => setFormData({...formData, stone_colour: e.target.value})}
              placeholder="e.g., Black, Grey"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Details about the memorial, condition, etc."
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* Cemetery Info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Cemetery / Location
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Cemetery Name *</Label>
            <Input
              value={formData.cemetery_name}
              onChange={(e) => setFormData({...formData, cemetery_name: e.target.value})}
              placeholder="Cemetery name"
              data-testid="removal-cemetery"
            />
          </div>
          <div>
            <Label>Cemetery Location</Label>
            <Input
              value={formData.cemetery_location}
              onChange={(e) => setFormData({...formData, cemetery_location: e.target.value})}
              placeholder="Town / area"
            />
          </div>
          <div>
            <Label>Plot Reference</Label>
            <Input
              value={formData.plot_reference}
              onChange={(e) => setFormData({...formData, plot_reference: e.target.value})}
              placeholder="Plot/Row/Section"
            />
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-medium text-purple-800 mb-3">Dates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Funeral Date</Label>
            <Input
              type="date"
              value={formData.funeral_date}
              onChange={(e) => setFormData({...formData, funeral_date: e.target.value})}
              data-testid="removal-funeral-date"
            />
            <p className="text-xs text-slate-500 mt-1">When is the funeral?</p>
          </div>
          <div>
            <Label>Scheduled Collection Date</Label>
            <Input
              type="date"
              value={formData.scheduled_collection_date}
              onChange={(e) => setFormData({...formData, scheduled_collection_date: e.target.value})}
              data-testid="removal-collection-date"
            />
            <p className="text-xs text-slate-500 mt-1">When to collect the stone — adds to calendar</p>
          </div>
        </div>
      </div>

      {/* Requestor Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
          <User className="w-4 h-4" /> Requestor Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Requestor Type</Label>
            <Select value={formData.requestor_type} onValueChange={(v) => setFormData({...formData, requestor_type: v})}>
              <SelectTrigger data-testid="requestor-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client / Family</SelectItem>
                <SelectItem value="funeral_director">Funeral Director</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Name *</Label>
            <Input
              value={formData.requestor_name}
              onChange={(e) => setFormData({...formData, requestor_name: e.target.value})}
              placeholder="Contact name"
              data-testid="removal-requestor-name"
            />
          </div>
          {formData.requestor_type === 'funeral_director' && (
            <div>
              <Label>Company</Label>
              <Input
                value={formData.requestor_company}
                onChange={(e) => setFormData({...formData, requestor_company: e.target.value})}
                placeholder="Funeral director company"
              />
            </div>
          )}
          <div>
            <Label>Phone</Label>
            <Input
              value={formData.requestor_phone}
              onChange={(e) => setFormData({...formData, requestor_phone: e.target.value})}
              placeholder="Phone number"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              value={formData.requestor_email}
              onChange={(e) => setFormData({...formData, requestor_email: e.target.value})}
              placeholder="Email address"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Removals;
